//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import { createContext, type Dispatch, type SetStateAction } from 'react';
import {
  type CreateGraphSchemaResponse, type DatasetInfo, type RawGraphSchema
} from '@/services/apiTypes';

// -----------------------------------------------------------------------
// Dataset context value.
//
// Holds the active dataset + dataset list state and the dataset CRUD methods.
// setActiveDataset is the canonical selection setter: it validates the value
// against the dataset list and persists it to the user profile before updating
// front-end state (so it is async and a no-op for an unchanged or unknown
// dataset — not a raw React state setter).
// -----------------------------------------------------------------------

export interface DatasetContextValue {
  // --- State ---
  activeDataset: string;
  setActiveDataset: (dataset: string) => Promise<void>;
  // datasetsInfo is backed by the TanStack Query cache and is read-only here
  // (no setter; the cache is populated by the datasets query in xgtQueries).
  datasetsInfo: DatasetInfo[];
  // The datasets somebody can choose to work on, which is not all of them.
  //
  // A namespace is where frames live, so the datasets page lists every one that holds
  // anything -- a namespace of nothing but query results included, since that is the only
  // place those can be reached from.  But choosing a dataset means choosing what the
  // explorer queries, what the schema view draws and what an upload lands in, and a
  // namespace with no vertex or edge frame is not an answer to any of those.
  //
  // Whatever is active is always among these, even where it would not otherwise be: a
  // selection made deliberately on the datasets page is not one to undo, and a select whose
  // value is not one of its options is a blank control.
  choosableDatasets: DatasetInfo[];
  // True during the datasets list's initial load — before activeDataset can be
  // derived — so consumers can show a loading state rather than an empty one.
  datasetsLoading: boolean;
  // True once the list has been fetched. Not the same as not loading: a fetch
  // that failed is not loading either, and its empty list says nothing.
  datasetsLoaded: boolean;

  // --- Demo dataset state ---
  demoIsLoading: Record<string, boolean>;
  setDemoIsLoading: Dispatch<SetStateAction<Record<string, boolean>>>;
  // demoMenuItemDisabled is derived (no setter).
  demoMenuItemDisabled: Record<string, boolean>;

  // --- Methods ---
  createDataset: (
    schema: RawGraphSchema, oldGraph?: string
  ) => Promise<CreateGraphSchemaResponse>;
  // Resolves with the frames the server would not empty -- an append-only
  // container a query wrote -- so the caller can say what was left as it was.
  clearDataset: (dataset_name: string) => Promise<string[]>;
  clearFrame: (frame_name: string) => Promise<void>;
  deleteDataset: (dataset_name: string) => Promise<void>;
  deleteFrame: (frame_name: string) => Promise<void>;
  createDemoData: (dataset_name: string) => Promise<string>;
}

const DatasetContext = createContext<DatasetContextValue | null>(null);

export { DatasetContext };
